import { useLanguage } from '@/hooks/useLanguage';
import { Card } from '@/components/ui/Card';
import { FadeInView } from '@/components/animations/FadeInView';

/**
 * 场景项数据接口
 */
interface UseCase {
  id: string;
  icon: string;
  titleKey: string;
  itemsKey: string;
}

/**
 * UseCasesSection 组件属性
 */
interface UseCasesSectionProps {
  className?: string;
}

/**
 * UseCasesSection 组件 - 展示产品的 6 大应用场景
 * 
 * 使用响应式网格布局：
 * - 桌面端（>= 1024px）: 3 列
 * - 平板端（768px - 1023px）: 2 列
 * - 移动端（< 768px）: 1 列
 * 
 * 每个场景卡片包含图标、标题和应用说明列表，并带有悬浮效果。
 * 使用 FadeInView 实现滚动进入视口时的淡入动画。
 */
export function UseCasesSection({ className = '' }: UseCasesSectionProps) {
  const { t } = useLanguage();

  // 定义 6 个场景项的图标（使用 emoji 作为图标）
  const useCases: UseCase[] = [
    {
      id: 'ecommerce',
      icon: '🛒',
      titleKey: 'useCases.items.0.title',
      itemsKey: 'useCases.items.0.items',
    },
    {
      id: 'market-research',
      icon: '📊',
      titleKey: 'useCases.items.1.title',
      itemsKey: 'useCases.items.1.items',
    },
    {
      id: 'recruitment',
      icon: '💼',
      titleKey: 'useCases.items.2.title',
      itemsKey: 'useCases.items.2.items',
    },
    {
      id: 'academic',
      icon: '🎓',
      titleKey: 'useCases.items.3.title',
      itemsKey: 'useCases.items.3.items',
    },
    {
      id: 'content-creation',
      icon: '✍️',
      titleKey: 'useCases.items.4.title',
      itemsKey: 'useCases.items.4.items',
    },
    {
      id: 'lead-generation',
      icon: '🎯',
      titleKey: 'useCases.items.5.title',
      itemsKey: 'useCases.items.5.items',
    },
  ];

  return (
    <section
      className={`py-20 px-4 md:px-8 lg:px-16 bg-gray-50 ${className}`}
    >
      <div className="max-w-7xl mx-auto">
        {/* 区域标题 */}
        <FadeInView>
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              {t('useCases.title')}
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              适用于多种场景，满足不同行业的数据提取需求
            </p>
          </div>
        </FadeInView>

        {/* 场景卡片网格 */}
        <div
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8"
          data-testid="use-cases-grid"
        >
          {useCases.map((useCase, index) => {
            // 获取应用说明列表
            const items = t(useCase.itemsKey);
            const itemsList = Array.isArray(items) ? items : [];

            return (
              <FadeInView key={useCase.id} delay={index * 100}>
                <Card hoverable data-testid="use-case-card" className="h-full">
                  {/* 场景图标 */}
                  <div
                    className="text-5xl mb-4"
                    data-testid="use-case-icon"
                    role="img"
                    aria-label={t(useCase.titleKey)}
                  >
                    {useCase.icon}
                  </div>

                  {/* 场景标题 */}
                  <h3
                    className="text-xl font-semibold text-gray-900 mb-3"
                    data-testid="use-case-title"
                  >
                    {t(useCase.titleKey)}
                  </h3>

                  {/* 应用说明列表 */}
                  <ul
                    className="space-y-2"
                    data-testid="use-case-items"
                  >
                    {itemsList.map((item, itemIndex) => (
                      <li
                        key={itemIndex}
                        className="flex items-start text-gray-600"
                      >
                        <span className="text-primary mr-2 mt-1 flex-shrink-0">•</span>
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                </Card>
              </FadeInView>
            );
          })}
        </div>
      </div>
    </section>
  );
}
